import React, { useContext, SyntheticEvent, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import Toolbar from "@mui/material/Toolbar";
import EditIcon from '@mui/icons-material/Edit';
import AddCommentIcon from '@mui/icons-material/AddComment';

//Custom Components
import DraggableDialog from "@/ui/Dialog/DraggableDialog";
import DialogHeader from "@/ui/Dialog/DialogHeader";
import DialogFooter from "@/ui/Dialog/DialogFooter";
import DialogToolbarHeading from "@/ui/Dialog/DialogToolbarHeading";
import DialogToolbarMenuButton from "@/ui/Dialog/DialogToolbarMenuButton";
import DialogToolbarMenuItemDelete from "@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDelete"
import DialogToolbarButtonClose from "@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose";
import ToolbarFillContent from "@/ui/Toolbar/ToolbarFillContent";
import DialogBody from "@/ui/Dialog/DialogBody";
import DialogActionButtonConfirm from "@/ui/Dialog/ActionButtons/DialogActionButtonConfirm";
import DialogActionButtonClose from "@/ui/Dialog/ActionButtons/DialogActionButtonClose";
import DialogActionButtonSave from "@/ui/Dialog/ActionButtons/DialogActionButtonSave";
import useFormController from "@/components/useFormController";
import GridContainer from "@/ui/Grid/GridContainer";
import FormContent from "@/components/FormContent";

import DialogContext from "@/ui/DialogContext/DialogContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import dataController from "@/lib/dataController";
import modelComments from "@/models/komentari"
import { IDialogProps } from "@/@types/ui/DialogContext";
import { Typography } from "@mui/material";
import WKT from "ol/format/WKT";

function CommentDialog(props: IDialogProps) {
  const { feature, mode, refreshLayers, recordId, onClose, defaultValues, flag, form, initialRecord } = props;

  const { t } = useTranslation();
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);

  const dc = new dataController(modelComments)

  const close = (result: any) => {
    if (onClose) {
      onClose(result);
    }

    dialogContext.hideDialog();
  };

  const {
    record,
    validation,
    dataChanged,
    fields,
    subModels,
    onFieldChange,
    doInsert,
    doUpdate,
    doClose,
    doDelete,
    doRefresh
  } = useFormController({
    mode: mode,
    recordId: recordId,
    form: form,
    dc: dc,
    onClose: close,
    initialRecord: initialRecord
  });


  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };


  const handleClose = (evt: SyntheticEvent) => {
    close({ success: false });
  }

  const handleInsert = (evt: SyntheticEvent) => {
    if (doInsert) {
      doInsert().then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t("notifications.insert"), "success");
          close({ dataChanged: true, action: "insert" });
        } else if (result.error) {
          const msg = t("error.addRecord") + result.error;
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          //do nothing
        }
      })
        .catch((resp) => {
          snackbarContext.showNotification(resp.error, "error");
        });
    }
  };


  const handleUpdate = (evt: SyntheticEvent) => {
    if (doUpdate) {
      doUpdate().then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t("notifications.update"), "success");
          close({ dataChanged: true, action: "update" });
        } else if (result.error) {
          const msg = t("error.editRecord") + result.error;
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          //do nothing
        }
      })
        .catch((resp) => {
          snackbarContext.showNotification(resp.error, "error");
        });
    }
  };

  const handleDelete = () => {
    doDelete()
      .then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t("notifications.delete"), "success");
          close({ dataChanged: true, action: "delete" });
        } else if (result.error) {
          const msg = t("error.delete") + result.error;
          snackbarContext.showNotification(msg, "error");
        }
      })
  }

  const title = mode === "insert" ? t("titles.add_comment")
    : mode === "update" ? t("titles.edit_comment")
      : mode === "view" ? t("titles.comment")
        : null

  const disableSave = dataChanged ? false : true;


  return (
    <DraggableDialog open={true} maxWidth={"sm"} onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters={true}>
          {mode == "update" ? <EditIcon sx={{ transform: "scale(1.3)" }} ></EditIcon> : mode == "insert" ? <AddCommentIcon sx={{ transform: "scale(1.3)" }} ></AddCommentIcon> : <></>}
          <DialogToolbarHeading>{title}</DialogToolbarHeading>
          <ToolbarFillContent />
          {mode === "update" ?
            <DialogToolbarMenuButton>
              <DialogToolbarMenuItemDelete onClick={handleDelete} />
            </DialogToolbarMenuButton>
            : null}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        <Box m={2}>
          <GridContainer>
            <FormContent
              fieldNames={dc.getFormFieldsNames(form)}
              // @ts-ignore
              record={record}
              validation={validation}
              fields={fields}
              onFieldChange={handleFieldChange}
              mode={mode}
              columns={2}
              subModels={subModels}
            />
          </GridContainer>
        </Box>
      </DialogBody>
      {mode === "insert" ?
        <DialogActions>
          <DialogActionButtonClose variant="outlined" onClick={handleClose} />
          <DialogActionButtonConfirm variant="contained" onClick={handleInsert} />
        </DialogActions>
        :
        <DialogFooter record={record}>
          <DialogActionButtonClose variant="outlined" onClick={handleClose} />
          {mode === "update" ?
            <DialogActionButtonSave variant="contained" onClick={handleUpdate} disabled={disableSave} />
            : null}
        </DialogFooter>}
    </DraggableDialog>
  )
}

export default CommentDialog
